<template>
  <div ref="item" class="war-room-post-poll-results-choice">
    <div class="war-room-post-poll-results-choice__header">
      <div class="war-room-post-poll-results-choice__title">
        <BravadoText tag="span" variant="subhead-semibold">
          {{ `${percentage}%` }}
        </BravadoText>
        <BravadoText tag="span" variant="subhead">
          {{ choice.text }}
        </BravadoText>
      </div>
    </div>

    <div class="war-room-post-poll-results-choice__progress">
      <CheckIconSvg
        v-if="choice.current_vote"
        class="war-room-post-poll-results-choice__check-icon"
        :style="{ opacity }"
      />
      <div
        class="war-room-post-poll-results-choice__rail"
        :class="{ 'war-room-post-poll-results-choice__rail--selected': choice.current_vote }"
      >
        <div :style="fillStyle" class="war-room-post-poll-results-choice__fill" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from '@nuxtjs/composition-api';
import type { StyleValue } from '@vue/runtime-dom';
import BravadoText from '@/components/common/BravadoText/BravadoText.vue';
import CheckIconSvg from '@/components/partials/war-room/WarRoomPostPollV2/WarRoomPostPollV2Results/images/check.vue.svg';
import type { IPostPoll, IPostPollChoice } from '@/types/community';

type WarRoomPostPollResultsChoiceProps = {
  poll: IPostPoll;
  choice: IPostPollChoice;
};

const props = defineProps<WarRoomPostPollResultsChoiceProps>();

const width = ref('');
const item = ref<HTMLElement | null>(null);
let timeoutId: number;

const percentage = computed(() => {
  // here it should be all the time
  const pollVotesCount = props.poll.votes_count;
  return pollVotesCount ? props.choice.votes_count && Math.round((props.choice.votes_count / pollVotesCount) * 100) : 0;
});

const opacity = computed(() => Math.min(1, (percentage.value / 100) * 0.7 + 0.3));
const fillStyle = computed<StyleValue>(() => ({
  width: width.value,
  opacity: `${opacity.value}`,
}));

function calculateWidth() {
  if (props.choice.current_vote && item.value) {
    // we need this bc width width check icon is different
    const { clientWidth } = item.value;
    let pxWidth = ((clientWidth + 13) * percentage.value) / 100 - 13;

    // sometimes progress less than check ison. so we set minimum 5px width for progress line
    if (pxWidth <= 0) {
      pxWidth = 5;
    }

    width.value = `${(pxWidth / clientWidth) * 100}%`;
  } else {
    width.value = `${percentage.value}%`;
  }
}

onMounted(() => {
  calculateWidth();
  // on mobile there is chance that layout will change width after loading, so we need calculate again
  timeoutId = window.setTimeout(calculateWidth, 1000);
});

onUnmounted(() => {
  window.clearTimeout(timeoutId);
});
</script>

<style scoped lang="scss">
.war-room-post-poll-results-choice {
  margin-top: 12px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    color: var(--theme-color-sub-text);
    margin-bottom: 4px;
    line-height: 20px;
  }

  &__progress {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
  }

  &__rail {
    background: var(--blue-60-a10);
    border-radius: 8px;
    height: 2px;
    width: 100%;

    &--selected {
      margin-left: 13px;
    }
  }

  &__fill {
    background: var(--blue-60);
    border-radius: 8px;
    height: 100%;
  }

  &__check-icon {
    position: absolute;
    left: 0;
    top: -4px;
    flex-shrink: 0;
    margin-right: 3px;
  }
}
</style>
